import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FinePrint } from "@/components/FinePrint";
import { Loader2, KeyRound, Copy, MailCheck, Search } from "lucide-react";
import { Loader2 as _L, Network, ShieldCheck, AlertTriangle, Download, Save, History, Sliders, Wand2 } from "lucide-react";

const STATUS_BADGE = {
  approved: "bg-emerald-100 text-emerald-800",
  needs_review: "bg-amber-100 text-amber-800",
  draft: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = { approved: "Approved", needs_review: "Needs review", draft: "Draft" };

function Stat({ icon: Icon, label, value, sub, tone = "#003057" }) {
  return (
    <Card className="p-5 card-elevate">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: `${tone}12` }}>
          <Icon className="h-5 w-5" style={{ color: tone }} />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
          <p className="font-mono-num text-2xl font-bold text-[#003057]">{value}</p>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function WeightingCard({ config, onSaved }) {
  const [emp, setEmp] = useState(config ? Math.round(config.employer_weight * 100) : 15);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (config) setEmp(Math.round(config.employer_weight * 100)); }, [config]);

  const save = async () => {
    setSaving(true);
    try {
      const ew = Math.max(0, Math.min(100, emp)) / 100;
      const { data } = await api.patch("/admin/occupation-config", { baseline_weight: Math.round((1 - ew) * 1000) / 1000, employer_weight: Math.round(ew * 1000) / 1000 });
      onSaved(data);
      toast.success(`Weighting saved — ${Math.round(data.baseline_weight * 100)}/${Math.round(data.employer_weight * 100)}`);
    } catch { toast.error("Could not save weighting"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6 card-elevate" data-testid="occ-weighting-card">
      <div className="mb-3 flex items-center gap-2">
        <Sliders className="h-4 w-4 text-[#78be21]" />
        <h3 className="font-heading text-lg font-bold text-[#003057]">Career Target weighting</h3>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        Each occupation's Career Target Profile blends the auditable GPA+ baseline with aggregated verified employer input.
        Adjust the split below (baseline + employer must total 100%).
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Baseline weight</label>
          <p className="font-mono-num text-3xl font-bold text-[#005291]" data-testid="baseline-weight-display">{100 - emp}%</p>
        </div>
        <div className="text-2xl font-bold text-slate-300">/</div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Employer weight</label>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={100} value={emp} onChange={(e) => setEmp(Number(e.target.value))}
              className="w-24 font-mono-num text-lg" data-testid="employer-weight-input" />
            <span className="text-lg font-bold text-slate-400">%</span>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#003057] hover:bg-[#00223f]" data-testid="save-weighting-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save</>}
        </Button>
      </div>
      <p className="mt-3 text-[11px] text-slate-400">
        Default 85 / 15. Employer input only shifts the target where an aggregated employer signal exists — it never changes any student's TSB.
      </p>
    </Card>
  );
}

function EditDialog({ soc, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [baseline, setBaseline] = useState({});
  const [source, setSource] = useState({ label: "", url: "", note: "" });
  const [status, setStatus] = useState("draft");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!soc) return;
    setDetail(null);
    api.get(`/admin/occupations/${soc}`).then(({ data }) => {
      setDetail(data);
      setBaseline({ ...(data.skill_demand || {}) });
      setSource({ label: data.source?.label || "", url: data.source?.url || "", note: data.source?.note || "" });
      setStatus(data.status || "draft");
      setNote("");
    });
  }, [soc]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/occupations/${soc}`, {
        skill_demand: baseline, source, status, note,
      });
      toast.success(`Saved — ${detail.local_title} v${data.version}`);
      onSaved();
      onClose();
    } catch { toast.error("Could not save occupation"); }
    finally { setSaving(false); }
  };

  const seedFromOnet = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post(`/admin/occupations/${soc}/onet-baseline`);
      const o = data.occupation;
      setDetail((d) => ({ ...d, ...o, versions: [{ version: o.version - 1, status: d.status, note: "Auto-snapshot before O*NET crosswalk baseline", changed_at: new Date().toISOString(), changed_by: "you" }, ...(d.versions || [])] }));
      setBaseline({ ...(o.skill_demand || {}) });
      setSource({ label: o.source?.label || "", url: o.source?.url || "", note: o.source?.note || "" });
      setStatus(o.status || "needs_review");
      toast.success(`Seeded from O*NET — ${data.elements_found} skills mapped (needs review)`);
      onSaved();
    } catch { toast.error("Could not reach O*NET to seed this baseline."); }
    finally { setSeeding(false); }
  };

  return (
    <Dialog open={!!soc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="occ-edit-dialog">
        {!detail && (
          <div className="flex h-48 flex-col items-center justify-center">
            <DialogTitle className="sr-only">Loading occupation</DialogTitle>
            <Loader2 className="h-7 w-7 animate-spin text-[#003057]" />
          </div>
        )}
        {detail && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#003057]">{detail.local_title}</DialogTitle>
              <p className="text-sm text-slate-500">{detail.title} · <span className="font-mono-num">{detail.onet_soc}</span> · v{detail.version}</p>
            </DialogHeader>

            <div className="space-y-5">
              <div>
                <p className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-400">
                  <span>Baseline skill demand (1.0–4.0)</span>
                  <Button type="button" size="sm" variant="outline" onClick={seedFromOnet} disabled={seeding}
                    className="h-7 border-[#78be21]/50 text-[#5fa114] hover:bg-[#78be21]/10" data-testid="seed-onet-btn">
                    {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Seed from O*NET</>}
                  </Button>
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="baseline-editor">
                  {(detail.skills_meta || []).map((s) => (
                    <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5">
                      <span className="text-sm font-semibold text-[#003057]">{s.name}</span>
                      <Input type="number" step="0.1" min="1" max="4"
                        value={baseline[s.id] ?? 2.5}
                        onChange={(e) => setBaseline((b) => ({ ...b, [s.id]: Number(e.target.value) }))}
                        className="w-20 font-mono-num" data-testid={`baseline-${s.id}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Status</p>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="occ-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft" data-testid="status-draft">Draft</SelectItem>
                      <SelectItem value="needs_review" data-testid="status-needs_review">Needs review</SelectItem>
                      <SelectItem value="approved" data-testid="status-approved">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Change note</p>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. validated against O*NET v30" data-testid="occ-change-note" />
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Data source</p>
                <Input value={source.label} onChange={(e) => setSource((s) => ({ ...s, label: e.target.value }))} placeholder="Source label" data-testid="source-label" />
                <Input value={source.url} onChange={(e) => setSource((s) => ({ ...s, url: e.target.value }))} placeholder="Source URL" data-testid="source-url" />
                <Input value={source.note} onChange={(e) => setSource((s) => ({ ...s, note: e.target.value }))} placeholder="Provenance note" data-testid="source-note" />
              </div>

              {(detail.versions || []).length > 0 && (
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-400"><History className="h-3.5 w-3.5" /> Version history</p>
                  <ul className="space-y-1 text-xs text-slate-500" data-testid="version-history">
                    {detail.versions.map((v, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span>v{v.version} · {STATUS_LABEL[v.status] || v.status} {v.note ? `· ${v.note}` : ""}</span>
                        <span className="text-slate-400">{(v.changed_at || "").slice(0, 10)} · {v.changed_by}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} data-testid="occ-cancel-btn">Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-[#003057] hover:bg-[#00223f]" data-testid="occ-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save & version</>}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminOccupations() {
  const [rows, setRows] = useState(null);
  const [config, setConfig] = useState(null);
  const [audit, setAudit] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [naicsFilter, setNaicsFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [seedingAll, setSeedingAll] = useState(false);
  const [approving, setApproving] = useState(false);
  const [aggregating, setAggregating] = useState(false);

  const aggregateEmployerSignal = async () => {
    setAggregating(true);
    try {
      const { data } = await api.post("/admin/occupations/aggregate-employer-signal");
      toast.success(`Employer signal set on ${data.occupations_with_signal} occupation${data.occupations_with_signal === 1 ? "" : "s"}`);
      load();
    } catch { toast.error("Could not aggregate employer signal"); }
    finally { setAggregating(false); }
  };

  const approveAllReviewed = async () => {
    setApproving(true);
    try {
      const { data } = await api.post("/admin/occupations/approve-all");
      toast.success(`Approved ${data.approved} occupation${data.approved === 1 ? "" : "s"}`);
      load();
    } catch { toast.error("Could not approve baselines"); }
    finally { setApproving(false); }
  };

  const seedAllFromOnet = async () => {
    setSeedingAll(true);
    try {
      const { data } = await api.post("/admin/occupations/onet-baseline-all");
      toast.success(`Seeded ${data.updated}/${data.total} baselines from O*NET${data.failed.length ? ` · ${data.failed.length} failed` : ""}`);
      load();
    } catch { toast.error("Could not reach O*NET to seed baselines."); }
    finally { setSeedingAll(false); }
  };

  const load = () => {
    api.get("/admin/occupations").then(({ data }) => setRows(data)).catch(() => setRows([]));
    api.get("/admin/occupation-audit").then(({ data }) => setAudit(data)).catch(() => setAudit(null));
  };
  useEffect(() => {
    load();
    api.get("/admin/occupation-config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/naics-industries").then(({ data }) => setIndustries(data)).catch(() => {});
  }, []);

  const filtered = (rows || []).filter((r) =>
    (naicsFilter === "all" || (r.industries || []).includes(naicsFilter)) &&
    (statusFilter === "all" || r.status === statusFilter));

  const downloadAudit = () => {
    if (!audit) return;
    const lines = [["O*NET-SOC", "Occupation", "Industry", "Status", "Version", "Programs", "Flags"]];
    audit.flagged.forEach((f) => lines.push([f.onet_soc, f.local_title, f.primary_industry, f.status, f.version, f.program_count, f.flags.join("; ")]));
    const csv = lines.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gpaplus-occupation-audit-${audit.generated_at.slice(0, 10)}.csv`;
    a.click();
    toast.success("Audit report downloaded");
  };

  return (
    <DashboardShell title="Career & Occupational Profiles"
      subtitle="Governed O*NET-SOC baselines, sources, versioning & pre-migration audit"
      nav={ADMIN_NAV}>
      {!rows && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {rows && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Stat icon={Network} label="Occupations" value={audit?.total_occupations ?? rows.length} sub="canonical O*NET-SOC" />
            <Stat icon={ShieldCheck} label="Migration-ready" value={audit?.ready_for_migration ?? 0} sub="fully validated" tone="#0d9488" />
            <Stat icon={AlertTriangle} label="Need attention" value={audit?.needs_attention ?? 0} sub="flagged" tone="#d97706" />
            <Stat icon={History} label="Approved" value={audit?.status_counts?.approved ?? 0} sub="of the catalog" tone="#78be21" />
          </div>

          <WeightingCard config={config} onSaved={(c) => setConfig(c)} />

          {audit && (
            <Card className="p-6 card-elevate" data-testid="occ-audit-card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#005291]" />
                  <h3 className="font-heading text-lg font-bold text-[#003057]">Pre-migration audit</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={approveAllReviewed} disabled={approving}
                    className="bg-[#003057] hover:bg-[#00223f]" data-testid="approve-all-btn">
                    {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="mr-1.5 h-4 w-4" /> Approve all reviewed</>}
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadAudit} data-testid="download-audit-btn">
                    <Download className="mr-1.5 h-4 w-4" /> Download report (CSV)
                  </Button>
                </div>
              </div>
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#78be21]/8 p-3">
                <Wand2 className="h-4 w-4 flex-shrink-0 text-[#5fa114]" />
                <p className="flex-1 text-xs text-slate-600">Seed empirically defensible baselines for every occupation from live O*NET analyst skill-importance data. Each is versioned and set to <b>needs review</b> for your sign-off.</p>
                <Button size="sm" onClick={seedAllFromOnet} disabled={seedingAll}
                  className="bg-[#5fa114] hover:bg-[#4c8410]" data-testid="seed-all-onet-btn">
                  {seedingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-1.5 h-4 w-4" /> Seed all from O*NET</>}
                </Button>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-[#005291]/8 p-3">
                <Network className="h-4 w-4 flex-shrink-0 text-[#005291]" />
                <p className="flex-1 text-xs text-slate-600">Roll up employer job-posting skill priorities into each occupation's <b>15% employer signal</b>, blended into the Career Target. Never changes any student's TSB.</p>
                <Button size="sm" onClick={aggregateEmployerSignal} disabled={aggregating}
                  className="bg-[#005291] hover:bg-[#00426f]" data-testid="aggregate-employer-btn">
                  {aggregating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Network className="mr-1.5 h-4 w-4" /> Refresh employer signal</>}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="audit-flag-totals">
                {Object.entries(audit.flag_totals || {}).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
                    {audit.flag_labels?.[k] || k} <b className="font-mono-num">{v}</b>
                  </span>
                ))}
                {Object.keys(audit.flag_totals || {}).length === 0 && (
                  <span className="text-sm text-emerald-700">All occupations pass the audit — ready to migrate.</span>
                )}
              </div>
            </Card>
          )}

          <Card className="p-2 card-elevate sm:p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2 px-2 pt-2">
              <Select value={naicsFilter} onValueChange={setNaicsFilter}>
                <SelectTrigger className="w-56" data-testid="filter-naics"><SelectValue placeholder="All industries" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All industries</SelectItem>
                  {industries.map((i) => <SelectItem key={i.code} value={i.code}>{i.title}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="needs_review">Needs review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                </SelectContent>
              </Select>
              <span className="ml-auto text-xs text-slate-400">{filtered.length} of {rows.length}</span>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Occupation</TableHead><TableHead>O*NET-SOC</TableHead><TableHead>Industry</TableHead>
                <TableHead className="text-center">Programs</TableHead><TableHead className="text-center">Version</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-center">Flags</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.onet_soc} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setEditing(o.onet_soc)} data-testid={`occ-row-${o.onet_soc}`}>
                    <TableCell className="font-semibold text-[#003057]">{o.local_title}<div className="text-xs font-normal text-slate-400">{o.title}</div></TableCell>
                    <TableCell className="font-mono-num text-xs text-slate-500">{o.onet_soc}</TableCell>
                    <TableCell className="text-sm text-slate-600">{o.primary_industry}</TableCell>
                    <TableCell className="text-center font-mono-num">{o.program_count}</TableCell>
                    <TableCell className="text-center font-mono-num">v{o.version}</TableCell>
                    <TableCell><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_BADGE[o.status] || STATUS_BADGE.draft}`}>{STATUS_LABEL[o.status] || o.status}</span></TableCell>
                    <TableCell className="text-center">
                      {o.flags?.length ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700"><AlertTriangle className="h-3 w-3" />{o.flags.length}</span> : <span className="text-emerald-600">✓</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <FinePrint>
            Occupations are canonical U.S. O*NET-SOC classifications (deduped across industries). Each baseline is auditable,
            versioned, and sourced; the configurable baseline/employer weighting shapes only the occupational Career Target Profile
            used for exploration. Nothing here alters a student's Transferable Skills Benchmark.
          </FinePrint>
        </div>
      )}
      <EditDialog soc={editing} onClose={() => setEditing(null)} onSaved={load} />
    </DashboardShell>
  );
}
